/**
 * @file ContactGroups.js
 * @version 3.11.4
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-21
 * @modified 2026-10-21
 */

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './ContactGroups.css';
import LoadingSpinner from './LoadingSpinner';
import LoadingBar from './LoadingBar';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import SearchableDropdown from './SearchableDropdown';
import Input from './Input';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';
import { matchesAllTerms } from '../utils/searchMatch';
import MultiSelectDropdown from './MultiSelectDropdown';
import { useSlideOutButton } from './SlideOutButton';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import GroupEditModal from './GroupEditModal';
import TickCheckbox from './TickCheckbox';
import { addressLine1 } from '../utils/formatAddress';
import { onEnterKey } from '../utils/enterKey';

// What a joint-account link participates in. Statements/Reports default on (legacy
// behaviour); Pricing/Credit are opt-in. Order matches the toggles shown in the UI.
const LINK_SCOPES = [
  { key: 'statements', label: 'Statements', icon: 'receipt_long' },
  { key: 'pricing', label: 'Pricing', icon: 'sell' },
  { key: 'credit', label: 'Credit', icon: 'credit_score' },
  { key: 'reports', label: 'Reports', icon: 'bar_chart' },
];

// The "Haulage Job Suppliers" group renders a special table (Rate £ / Customers /
// Contact Groups + inline edit) backed by the /api/contacts/haulage-suppliers
// endpoints rather than the generic membership list.
const HAULAGE_SUPPLIERS_GROUP = 'Haulage Job Suppliers';

// What Clear Filters resets to: the group-detail search plus the summary's
// joint-account and supplier-group filters.
const CLEARED_FILTERS = { search: '', jointSearch: '', supplierSearch: '' };

function ContactGroups() {
  const [groupSummaries, setGroupSummaries] = useState([]);
  const [haulageSuppliers, setHaulageSuppliers] = useState([]);
  const [editingSupplierId, setEditingSupplierId] = useState(null);
  const [supplierDraft, setSupplierDraft] = useState({ rate: '', rates: [], customerIds: [], groups: [] });
  const [savingSupplierId, setSavingSupplierId] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupContacts, setGroupContacts] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [jointAccounts, setJointAccounts] = useState([]);
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Separate from searchTerm, which belongs to the group DETAIL view and is sent
  // to the server. These two filter lists already in hand.
  const [jointSearch, setJointSearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSupplierGroupModal, setShowSupplierGroupModal] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [addSupplierParentId, setAddSupplierParentId] = useState(null);

  useSlideOutButton([showLinkModal, showAddModal, showSupplierGroupModal, showAddSupplierModal, loading, selectedGroup]);

  // Contact / supplier options for dropdowns. Show address line 1 next to the
  // name (like the contract-pricing customer dropdown) and include it in search.
  const toOption = (c) => {
    const addr = addressLine1(c.address);
    return {
      value: c.id,
      badge: c.account_no || '',
      label: c.company_name || '',
      address: c.address,
      renderLabel: (
        <>{c.company_name || ''}{addr && <span className="address-line"> ({addr})</span>}</>
      ),
      searchText: `${c.account_no || ''} ${c.company_name || ''} ${addr}`
    };
  };
  const contactOptions = allContacts.map(toOption);
  // Options for the Haulage Job Suppliers inline editor.
  const haulageCustomerOptions = allContacts.map(c => ({
    value: c.id,
    label: c.company_name || c.account_no || `#${c.id}`,
    searchText: `${c.account_no || ''} ${c.company_name || ''}`.trim()
  }));
  const haulageGroupOptions = groupSummaries
    .filter(g => g.group_name && g.group_name !== HAULAGE_SUPPLIERS_GROUP)
    .map(g => ({ value: g.group_name, label: g.group_name }));
  const supplierOptions = suppliers.map(toOption);

  const [selectedParent, setSelectedParent] = useState('');
  // Children chosen when creating a new joint account (multiple).
  const [selectedJointChildren, setSelectedJointChildren] = useState([]);
  // Scope flags chosen when creating a new joint account (applied to all children).
  const [linkScopes, setLinkScopes] = useState({ statements: true, reports: true, pricing: false, credit: false });
  // Open group editor: { type: 'joint'|'supplier', group }
  const [editGroup, setEditGroup] = useState(null);
  const [selectedContactToAdd, setSelectedContactToAdd] = useState('');
  // Tracks the in-flight "add to group" call so the modal's confirm button
  // can be disabled and re-clicks can't fire a second insert (which is what
  // produced the triple-row Murch Bros (Engineers) Ltd duplicate before the
  // unique constraint and route bug fix landed).
  const [isAddingToGroup, setIsAddingToGroup] = useState(false);
  const [addProgress, setAddProgress] = useState(0);
  const [addMessage, setAddMessage] = useState('');
  const [selectedSupplierParent, setSelectedSupplierParent] = useState('');
  const [selectedSupplierChildren, setSelectedSupplierChildren] = useState([]);
  const [selectedSupplierToAdd, setSelectedSupplierToAdd] = useState('');
  const [isAddingToSupplierGroup, setIsAddingToSupplierGroup] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', type: 'warning', confirmText: 'Confirm', onConfirm: null });
  const closeConfirm = () => setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  const [removingContactId, setRemovingContactId] = useState(null);
  const [expandedParents, setExpandedParents] = useState(new Set());
  const [expandedSupplierParents, setExpandedSupplierParents] = useState(new Set());
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncMessage, setSyncMessage] = useState('');
  const [showSyncWarning, setShowSyncWarning] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Member search fetches as you type: 300ms after the last keystroke.
  useEffect(() => {
    if (selectedGroup === HAULAGE_SUPPLIERS_GROUP) {
      fetchHaulageSuppliers();
      return undefined;
    }
    if (!selectedGroup) return undefined;
    const t = setTimeout(() => fetchGroupContacts(selectedGroup, searchTerm), searchTerm ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedGroup]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchGroupSummaries(),
      fetchJointAccounts(),
      fetchSupplierGroups(),
      fetchStats(),
      fetchAllContacts(),
      fetchSuppliers()
    ]);
    setLoading(false);
  };

  const fetchGroupSummaries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/group-summaries`, { credentials: 'include' });
      setGroupSummaries(await response.json());
    } catch (error) { console.error('Failed to fetch group summaries:', error); }
  };

  // Responses come back out of order; only the newest may write the members.
  const latestMembers = useLatestRequest();

  const fetchGroupContacts = async (groupName, search = '') => {
    const seq = latestMembers.begin();
    try {
      const url = new URL(`${API_URL}/api/contacts/group/${encodeURIComponent(groupName)}/contacts`);
      if (search) url.searchParams.append('search', search);
      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();
      if (!latestMembers.isLatest(seq)) return;   // superseded by a later search
      setGroupContacts(data);
    } catch (error) {
      if (!latestMembers.isLatest(seq)) return;
      console.error('Failed to fetch group contacts:', error);
    }
  };

  const fetchHaulageSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/haulage-suppliers`, { credentials: 'include' });
      setHaulageSuppliers(await response.json());
    } catch (error) { console.error('Failed to fetch haulage suppliers:', error); }
  };

  const fetchAllContacts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts?limit=10000`, { credentials: 'include' });
      const data = await response.json();
      setAllContacts(data.contacts || []);
    } catch (error) { console.error('Failed to fetch all contacts:', error); }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/suppliers-list`, { credentials: 'include' });
      setSuppliers(await response.json());
    } catch (error) { console.error('Failed to fetch suppliers:', error); }
  };

  const fetchJointAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/joint-accounts`, { credentials: 'include' });
      setJointAccounts(await response.json());
    } catch (error) { console.error('Failed to fetch joint accounts:', error); }
  };

  const fetchSupplierGroups = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/supplier-groups`, { credentials: 'include' });
      setSupplierGroups(await response.json());
    } catch (error) { console.error('Failed to fetch supplier groups:', error); }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/stats`, { credentials: 'include' });
      setStats(await response.json());
    } catch (error) { console.error('Failed to fetch stats:', error); }
  };

  // ─── Contact Group Handlers ────────────────────────────────────────────────

  const handleBackToSummary = () => {
    setSelectedGroup(null);
    setGroupContacts([]);
    setSearchTerm('');
  };

  const handleRemoveFromGroup = (contactId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Contact',
      message: 'Are you sure you want to remove this contact from the group?',
      type: 'warning',
      confirmText: 'Remove',
      onConfirm: async () => {
        closeConfirm();
        setRemovingContactId(contactId);
        const groupName = selectedGroup;
        try {
          const response = await fetch(`${API_URL}/api/contacts/group/${encodeURIComponent(groupName)}/remove/${contactId}`, {
            method: 'DELETE', credentials: 'include'
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.error || 'Request failed');
          // Optimistic local updates — no re-fetch needed.
          setGroupContacts(prev => prev.filter(c => c.id !== contactId));
          setGroupSummaries(prev => prev.map(g =>
            g.group_name === groupName
              ? { ...g, contact_count: Math.max(0, (Number(g.contact_count) || 0) - 1) }
              : g
          ));
          // Success is silent — the row disappearing is feedback enough. Only
          // surface a warning if the removal couldn't be pushed to Xero.
          if (data.xeroWarning) {
            setAlertDialog({ isOpen: true, message: data.xeroWarning, type: 'warning' });
          }
        } catch (error) {
          console.error('Failed to remove contact from group:', error);
          setAlertDialog({ isOpen: true, message: 'Failed to remove contact from group', type: 'error' });
        } finally {
          setRemovingContactId(null);
        }
      }
    });
  };

  const handleAddToGroup = () => {
    if (!selectedContactToAdd || isAddingToGroup) return;
    setIsAddingToGroup(true);
    setAddProgress(5);
    setAddMessage('Connecting…');

    // Stream the add so the user sees each real stage (save → push to Xero →
    // sync that group back). EventSource is GET-only, so contactId is a query param.
    const groupName = selectedGroup;
    const url = new URL(`${API_URL}/api/contacts/group/${encodeURIComponent(groupName)}/add-stream`);
    url.searchParams.append('contactId', selectedContactToAdd);
    const eventSource = new EventSource(url, { withCredentials: true });

    // Track whether we closed deliberately, so the server ending the stream
    // (which the browser surfaces as onerror) isn't reported as a failure.
    let done = false;
    const closeStream = () => { done = true; eventSource.close(); };
    const resetState = () => { setIsAddingToGroup(false); setAddProgress(0); setAddMessage(''); };

    eventSource.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'progress') {
        setAddProgress(data.percentage || 0);
        setAddMessage(data.message || '');
      } else if (data.type === 'complete') {
        // Close synchronously BEFORE the awaits below — once the server calls
        // res.end() the browser fires onerror, so we must mark done first.
        closeStream();
        setAddProgress(100);
        setAddMessage('Complete');
        const result = data.result || {};
        await fetchGroupSummaries();
        await fetchGroupContacts(groupName, searchTerm);
        // Close the modal first, then drop the loading state — avoids the
        // dropdown flashing back into view for a frame before the modal unmounts.
        setShowAddModal(false);
        setSelectedContactToAdd('');
        resetState();
        if (result.xeroWarning) {
          setAlertDialog({ isOpen: true, message: result.xeroWarning, type: 'warning' });
        } else if (result.syncedToXero) {
          setAlertDialog({ isOpen: true, message: 'Contact added to group and synced with Xero.', type: 'success' });
        } else {
          setAlertDialog({ isOpen: true, message: 'Contact added to group.', type: 'success' });
        }
      } else if (data.type === 'error') {
        closeStream();
        resetState();
        setAlertDialog({ isOpen: true, message: `Failed to add contact to group: ${data.message}`, type: 'error' });
      }
    };

    eventSource.onerror = () => {
      if (done) return;
      closeStream();
      resetState();
      setAlertDialog({ isOpen: true, message: 'Connection lost while adding contact to group', type: 'error' });
    };
  };

  // ─── Sync Contact Groups from Xero ─────────────────────────────────────────

  const syncContactGroups = async () => {
    setSyncProgress(5);
    setSyncMessage('Connecting to Xero...');
    try {
      const eventSource = new EventSource(`${API_URL}/api/sync/contact-groups`, { withCredentials: true });
      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'progress') {
          setSyncProgress(data.percentage || 0);
          setSyncMessage(data.message || '');
        } else if (data.type === 'complete') {
          setSyncProgress(100);
          setSyncMessage('Complete');
          eventSource.close();
          const r = data.result;
          setAlertDialog({
            isOpen: true,
            message: `Synced ${r.groupsInXero} groups from Xero. ${r.contactsWithGroups} contacts updated.`,
            type: 'success'
          });
          fetchData();
          setTimeout(() => { setSyncProgress(0); setSyncMessage(''); }, 2000);
        } else if (data.type === 'error') {
          eventSource.close();
          setSyncProgress(0);
          setSyncMessage('');
          setAlertDialog({ isOpen: true, message: `Sync failed: ${data.message}`, type: 'error' });
        }
      };
      eventSource.onerror = () => {
        eventSource.close();
        setSyncProgress(0);
        setSyncMessage('');
        setAlertDialog({ isOpen: true, message: 'Connection lost during group sync', type: 'error' });
      };
    } catch (error) {
      setSyncProgress(0);
      setSyncMessage('');
      setAlertDialog({ isOpen: true, message: `Failed to sync: ${error.message}`, type: 'error' });
    }
  };

  const handleGroupClick = (groupName) => {
    setSelectedGroup(groupName);
    setSearchTerm('');
    setEditingSupplierId(null);
    if (groupName === HAULAGE_SUPPLIERS_GROUP) {
      fetchHaulageSuppliers();
    } else {
      fetchGroupContacts(groupName);
    }
  };

  // ─── Haulage supplier inline edit ──────────────────────────────────────────
  const startEditSupplier = (s) => {
    setEditingSupplierId(s.id);
    setSupplierDraft({
      rate: s.default_rate ?? '',
      rates: (s.rates || []).map(r => ({ postcode_prefix: r.postcode_prefix, rate: r.rate })),
      customerIds: (s.customers || []).map(c => c.id),
      groups: s.groups || []
    });
  };

  const cancelEditSupplier = () => {
    setEditingSupplierId(null);
    setSupplierDraft({ rate: '', rates: [], customerIds: [], groups: [] });
  };

  const setRateRow = (idx, field, value) =>
    setSupplierDraft(d => ({ ...d, rates: d.rates.map((r, i) => i === idx ? { ...r, [field]: value } : r) }));
  const addRateRow = () => setSupplierDraft(d => ({ ...d, rates: [...d.rates, { postcode_prefix: '', rate: '' }] }));
  const removeRateRow = (idx) => setSupplierDraft(d => ({ ...d, rates: d.rates.filter((_, i) => i !== idx) }));

  const saveEditSupplier = async (accountId) => {
    setSavingSupplierId(accountId);
    try {
      const response = await fetch(`${API_URL}/api/contacts/haulage-suppliers/${accountId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rate: supplierDraft.rate === '' ? null : parseFloat(supplierDraft.rate),
          rates: supplierDraft.rates
            .filter(r => (r.postcode_prefix || '').trim() && r.rate !== '' && r.rate != null)
            .map(r => ({ postcode_prefix: r.postcode_prefix.trim(), rate: parseFloat(r.rate) })),
          customerIds: supplierDraft.customerIds,
          groups: supplierDraft.groups
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Request failed');
      if (data.xeroWarning) setAlertDialog({ isOpen: true, message: data.xeroWarning, type: 'warning' });
      setEditingSupplierId(null);
      await fetchHaulageSuppliers();
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to save: ${error.message}`, type: 'error' });
    } finally {
      setSavingSupplierId(null);
    }
  };

  // ─── Joint Account Handlers ────────────────────────────────────────────────

  // Turn a 409 conflicts payload into a readable alert message.
  const conflictMessage = (data) =>
    (data.conflicts && data.conflicts.length)
      ? `${data.error || 'Cannot create group'}: ` + data.conflicts.map(c => `${c.name} ${c.reason}`).join('; ')
      : (data.error || 'Operation failed');

  const createJointAccount = async () => {
    if (!selectedParent || selectedJointChildren.length === 0) return;
    try {
      const res = await fetch(`${API_URL}/api/contacts/joint-accounts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ parentId: selectedParent, childIds: selectedJointChildren, scopes: linkScopes })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setAlertDialog({ isOpen: true, message: conflictMessage(data), type: 'error' }); return; }
      await fetchJointAccounts();
      setShowLinkModal(false);
      setSelectedParent('');
      setSelectedJointChildren([]);
      setLinkScopes({ statements: true, reports: true, pricing: false, credit: false });
    } catch (error) {
      console.error('Failed to create joint account:', error);
      setAlertDialog({ isOpen: true, message: 'Failed to create joint account', type: 'error' });
    }
  };

  // Toggle a single scope flag on an existing joint-account link (optimistic; reverts on error).
  const handleToggleScope = async (childId, scope, value) => {
    setJointAccounts(prev => prev.map(g => ({
      ...g,
      children: g.children.map(c => c.id === childId ? { ...c, [`link_${scope}`]: value } : c)
    })));
    try {
      await fetch(`${API_URL}/api/contacts/link-accounts/${childId}/scopes`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ [scope]: value })
      });
    } catch (error) {
      console.error('Failed to update link scope:', error);
      fetchJointAccounts(); // revert to server truth
    }
  };

  const toggleExpand = (id, setter) => {
    setter(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ─── Supplier Group Handlers ───────────────────────────────────────────────

  const handleCreateSupplierGroup = async () => {
    if (!selectedSupplierParent || selectedSupplierChildren.length === 0) return;
    try {
      const res = await fetch(`${API_URL}/api/contacts/supplier-groups`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ parentId: selectedSupplierParent, childIds: selectedSupplierChildren })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setAlertDialog({ isOpen: true, message: conflictMessage(data), type: 'error' }); return; }
      await fetchSupplierGroups();
      setShowSupplierGroupModal(false);
      setSelectedSupplierParent('');
      setSelectedSupplierChildren([]);
    } catch (error) {
      console.error('Failed to create supplier group:', error);
      setAlertDialog({ isOpen: true, message: 'Failed to create supplier group', type: 'error' });
    }
  };

  const handleAddToSupplierGroup = async () => {
    if (!addSupplierParentId || !selectedSupplierToAdd || isAddingToSupplierGroup) return;
    setIsAddingToSupplierGroup(true);
    try {
      const res = await fetch(`${API_URL}/api/contacts/supplier-groups/${addSupplierParentId}/add`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ childId: selectedSupplierToAdd })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      await fetchSupplierGroups();
      setShowAddSupplierModal(false);
      setSelectedSupplierToAdd('');
      setAddSupplierParentId(null);
      setAlertDialog({ isOpen: true, message: 'Supplier added to group.', type: 'success' });
    } catch (error) {
      console.error('Failed to add to supplier group:', error);
      setAlertDialog({ isOpen: true, message: `Failed to add supplier to group: ${error.message}`, type: 'error' });
    } finally {
      setIsAddingToSupplierGroup(false);
    }
  };

  const handleRemoveFromSupplierGroup = (parentId, childId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Supplier',
      message: 'Remove this supplier from the group?',
      type: 'warning',
      confirmText: 'Remove',
      onConfirm: async () => {
        closeConfirm();
        try {
          await fetch(`${API_URL}/api/contacts/supplier-groups/${parentId}/remove/${childId}`, {
            method: 'DELETE', credentials: 'include'
          });
          await fetchSupplierGroups();
        } catch (error) {
          console.error('Failed to remove from supplier group:', error);
          setAlertDialog({ isOpen: true, message: 'Failed to remove supplier from group', type: 'error' });
        }
      }
    });
  };

  const handleDeleteSupplierGroup = (parentId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Supplier Group',
      message: 'Delete this entire supplier group?',
      type: 'error',
      confirmText: 'Delete',
      onConfirm: async () => {
        closeConfirm();
        try {
          await fetch(`${API_URL}/api/contacts/supplier-groups/${parentId}`, {
            method: 'DELETE', credentials: 'include'
          });
          await fetchSupplierGroups();
        } catch (error) {
          console.error('Failed to delete supplier group:', error);
          setAlertDialog({ isOpen: true, message: 'Failed to delete supplier group', type: 'error' });
        }
      }
    });
  };

  // ─── Expandable parent/child list (reused for both sections) ───────────────

  /**
   * Groups matching a search, matched on the CHILDREN as well as the parent.
   *
   * Somebody looking for "Trewithen" is as likely to be looking at a linked
   * child account as at the parent that heads the group — a filter that only
   * read the parent row would hide the group containing what they searched for.
   */
  const filterGroups = (items, term) => {
    if (!term.trim()) return items;
    return items.filter((g) =>
      matchesAllTerms([g.parent_name, g.parent_account_no, addressLine1(g.parent_address)], term)
      || (g.children || []).some((c) =>
        matchesAllTerms([c.company_name, c.account_no, addressLine1(c.address)], term)));
  };

  const visibleJoint = useMemo(
    () => filterGroups(jointAccounts, jointSearch),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jointAccounts, jointSearch]);
  const visibleSupplierGroups = useMemo(
    () => filterGroups(supplierGroups, supplierSearch),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supplierGroups, supplierSearch]);

  /**
   * A group and its members, as a table.
   *
   * These two panels were the last lists in the system built out of divs, which
   * meant no sticky header, no column alignment between the two of them, and a
   * header that scrolled away with the rows. Both are now .data-table with
   * .expandable-row / .expanded-content, the same shape as every other table
   * here — one renderer, so joint accounts and supplier groups cannot drift
   * apart again.
   */
  const renderParentChildList = (items, expandedSet, expandSetter, options = {}) => {
    const { showRemove, onRemove, showAdd, showDelete, onDelete,
            showScopes, onToggleScope, showEdit, onEdit } = options;
    // Every row carries the same action slots — disabled where the action does
    // not apply — so the two panels' columns line up.
    const actionCount = (showEdit ? 1 : 0) + (showDelete ? 1 : 0);

    return (
      <div className="table-container cg-table-scroll">
        <table className="data-table cg-groups-table">
          <thead>
            <tr>
              <th className="expand-col" />
              <th>Account</th>
              <th className="cg-col-no">No.</th>
              <th className="cg-col-count">Linked</th>
              {actionCount > 0 && <th className="actions-col" />}
            </tr>
          </thead>
          <tbody>
            {items.map(group => {
              const isExpanded = expandedSet.has(group.parent_id);
              const colSpan = 4 + (actionCount > 0 ? 1 : 0);
              return (
                <React.Fragment key={group.parent_id}>
                  <tr className={`expandable-row${isExpanded ? ' expanded' : ''}`}
                      onClick={() => toggleExpand(group.parent_id, expandSetter)}>
                    <td className="expand-col">
                      <span className={`material-symbols-rounded expand-icon ${isExpanded ? 'expanded' : ''}`}>
                        chevron_right
                      </span>
                    </td>
                    <td>
                      <span className="cg-parent-name">{group.parent_name}</span>
                      {addressLine1(group.parent_address) && (
                        <span className="cg-parent-address">{addressLine1(group.parent_address)}</span>
                      )}
                    </td>
                    <td className="cg-col-no">
                      {group.parent_account_no && (
                        <span className="account-badge-inline">{group.parent_account_no}</span>
                      )}
                    </td>
                    <td className="cg-col-count">{group.children.length}</td>
                    {actionCount > 0 && (
                      <td>
                        <div className="actions-cell">
                          {showEdit && (
                            <button className="btn-icon" title="Edit group"
                              onClick={(e) => { e.stopPropagation(); onEdit(group); }}>
                              <span className="material-symbols-rounded">edit</span>
                            </button>
                          )}
                          {showDelete && (
                            <button className="btn-icon btn-danger" title="Delete group"
                              onClick={(e) => { e.stopPropagation(); onDelete(group.parent_id); }}>
                              <span className="material-symbols-rounded">delete</span>
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>

                  <tr className={`expanded-content${isExpanded ? ' expanded' : ''}`}>
                    <td colSpan={colSpan}>
                      {isExpanded && (showScopes ? (
                        <div className="scope-matrix">
                          <div className="scope-row scope-row-head">
                            <span className="scope-cell-name" />
                            {LINK_SCOPES.map(sc => (
                              <span key={sc.key} className="scope-cell scope-col-head material-symbols-rounded"
                                    title={sc.label}>{sc.icon}</span>
                            ))}
                          </div>
                          {group.children.map(child => (
                            <div key={child.id} className="scope-row">
                              <span className="scope-cell-name" title={child.company_name}>
                                <span className="scope-cell-line">
                                  {child.company_name}{child.account_no && <em>{child.account_no}</em>}
                                </span>
                                {addressLine1(child.address) && (
                                  <span className="scope-cell-address">{addressLine1(child.address)}</span>
                                )}
                              </span>
                              {LINK_SCOPES.map(sc => (
                                <span key={sc.key} className="scope-cell">
                                  <TickCheckbox id={`link-${child.id}-${sc.key}`} size={16}
                                    checked={!!child[`link_${sc.key}`]}
                                    onChange={(e) => onToggleScope(child.id, sc.key, e.target.checked)}
                                    ariaLabel={`${child.company_name} — ${sc.label}`} title={sc.label} />
                                </span>
                              ))}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="cg-children">
                          {group.children.map(child => (
                            <div key={child.id} className="child-account-row">
                              <span className="material-symbols-rounded">subdirectory_arrow_right</span>
                              <span className="child-name">
                                {child.company_name}
                                {addressLine1(child.address) && (
                                  <span className="child-address"> · {addressLine1(child.address)}</span>
                                )}
                              </span>
                              {child.account_no && <span className="child-account-no">({child.account_no})</span>}
                              {showRemove && (
                                <button className="btn-icon btn-danger child-remove" title="Remove"
                                  onClick={() => onRemove(group.parent_id, child.id)}>
                                  <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>close</span>
                                </button>
                              )}
                            </div>
                          ))}
                          {showAdd && (
                            <button className="add-child-btn"
                              onClick={() => { setAddSupplierParentId(group.parent_id); setShowAddSupplierModal(true); }}>
                              <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>add</span>
                              Add Supplier
                            </button>
                          )}
                        </div>
                      ))}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan={4 + (actionCount > 0 ? 1 : 0)} className="cg-no-results">
                  No groups match
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) return <LoadingSpinner />;

  // ─── Group Detail View ─────────────────────────────────────────────────────

  if (selectedGroup) {
    return (
      <div className="contact-groups">
        <div className="group-detail-header">
          <div>
            <button onClick={handleBackToSummary} className="btn-slideout">
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">arrow_back</span>
              </div>
              <span className="btn-label">Back to Groups</span>
            </button>
          </div>
          <h2>{selectedGroup}</h2>
          <div>
            <button onClick={() => setShowAddModal(true)} className="btn-slideout">
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">person_add</span>
              </div>
              <span className="btn-label">Add Contact</span>
            </button>
          </div>
        </div>

        {selectedGroup !== HAULAGE_SUPPLIERS_GROUP && (
          <div className="search-box">
            <span className="material-symbols-rounded">search</span>
            <input type="text" placeholder="Search contacts in this group..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={onEnterKey(() => {
                // Enter searches now rather than after the typing pause.
                if (selectedGroup) fetchGroupContacts(selectedGroup, searchTerm);
              })} />
            <SearchHelp fields={['company', 'account no.']} />
          </div>
        )}

        {selectedGroup === HAULAGE_SUPPLIERS_GROUP ? (
          <div className="table-container" style={{ overflow: 'visible' }}>
            <table className="search-results-table haulage-suppliers-table">
              <thead>
                <tr>
                  <th className="no-sort">Company Name</th>
                  <th className="no-sort">Default Rate (£)</th>
                  <th className="no-sort">Postcode Rates</th>
                  <th className="no-sort">Customers</th>
                  <th className="no-sort">Contact Groups</th>
                  <th className="actions-col no-sort">Actions</th>
                </tr>
              </thead>
              <tbody>
                {haulageSuppliers.map(s => {
                  const isEditing = editingSupplierId === s.id;
                  const isSaving = savingSupplierId === s.id;
                  return (
                    <tr key={s.id}>
                      <td>
                        <div className="cell-primary">{s.company_name}</div>
                        <div className="cell-sub">{s.account_no}</div>
                      </td>
                      <td>
                        {isEditing ? (
                          <input type="number" step="0.01" min="0" className="inline-rate-input"
                            value={supplierDraft.rate}
                            onChange={(e) => setSupplierDraft(d => ({ ...d, rate: e.target.value }))}
                            placeholder="-.--" />
                        ) : (
                          s.default_rate != null ? `£${Number(s.default_rate).toFixed(2)}` : '—'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <div className="rate-editor">
                            {supplierDraft.rates.map((r, idx) => (
                              <div key={idx} className="rate-row">
                                <input type="text" className="inline-prefix-input" value={r.postcode_prefix}
                                  onChange={(e) => setRateRow(idx, 'postcode_prefix', e.target.value.toUpperCase())}
                                  placeholder='Prefix' maxLength={4} />
                                <input type="number" step="0.01" min="0" className="inline-rate-input" value={r.rate}
                                  onChange={(e) => setRateRow(idx, 'rate', e.target.value)} placeholder="-.--" />
                                <button className="btn-icon btn-danger" title="Remove" onClick={() => removeRateRow(idx)}>
                                  <span className="material-symbols-rounded">remove</span>
                                </button>
                              </div>
                            ))}
                            <button className="btn-icon" title="Add postcode rate" onClick={addRateRow}>
                              <span className="material-symbols-rounded">add</span>
                            </button>
                          </div>
                        ) : (
                          <div className="rate-display">
                            {(s.rates && s.rates.length)
                              ? s.rates.map(r => (
                                  <span key={r.postcode_prefix} className="rate-chip">{r.postcode_prefix} £{Number(r.rate).toFixed(2)}</span>
                                ))
                              : '—'}
                          </div>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <MultiSelectDropdown
                            options={haulageCustomerOptions}
                            value={supplierDraft.customerIds}
                            onChange={(vals) => setSupplierDraft(d => ({ ...d, customerIds: vals }))}
                            placeholder="Assign customers…"
                            searchable
                            searchPlaceholder="Search customers…"
                            reflowOnScroll
                          />
                        ) : (
                          (s.customers && s.customers.length)
                            ? s.customers.map(c => c.company_name).join(', ')
                            : '—'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <MultiSelectDropdown
                            options={haulageGroupOptions}
                            value={supplierDraft.groups}
                            onChange={(vals) => setSupplierDraft(d => ({ ...d, groups: vals }))}
                            placeholder="Assign groups…"
                            searchable
                            searchPlaceholder="Search groups…"
                            reflowOnScroll
                          />
                        ) : (
                          (s.groups && s.groups.length) ? s.groups.join(', ') : '—'
                        )}
                      </td>
                      <td className="actions-col">
                        <div className="actions-cell">
                          {isEditing ? (
                            <>
                              <button onClick={() => saveEditSupplier(s.id)} className="btn-icon btn-success"
                                title="Save" disabled={isSaving}>
                                <span className={`material-symbols-rounded${isSaving ? ' spinning' : ''}`}>
                                  {isSaving ? 'progress_activity' : 'save'}
                                </span>
                              </button>
                              <button onClick={cancelEditSupplier} className="btn-icon"
                                title="Cancel" disabled={isSaving}>
                                <span className="material-symbols-rounded">close</span>
                              </button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => startEditSupplier(s)} className="btn-icon"
                                title="Edit rate, customers & groups">
                                <span className="material-symbols-rounded">edit</span>
                              </button>
                              <button onClick={() => handleRemoveFromGroup(s.id)} className="btn-icon btn-danger"
                                title={removingContactId === s.id ? 'Removing…' : 'Remove from group'}
                                disabled={removingContactId === s.id}>
                                <span className={`material-symbols-rounded${removingContactId === s.id ? ' spinning' : ''}`}>
                                  {removingContactId === s.id ? 'progress_activity' : 'person_remove'}
                                </span>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {haulageSuppliers.length === 0 && <div className="no-results">No suppliers in this group</div>}
          </div>
        ) : (
        <div className="table-container">
          <table className="search-results-table">
            <thead>
              <tr>
                <th className="no-sort">Account No</th>
                <th className="no-sort">Company Name</th>
                <th className="no-sort">Email</th>
                <th className="no-sort">Phone</th>
                <th className="actions-col no-sort">Actions</th>
              </tr>
            </thead>
            <tbody>
              {groupContacts.map(contact => (
                <tr key={contact.id}>
                  <td>{contact.account_no}</td>
                  <td>{contact.company_name}</td>
                  <td>{contact.email || '-'}</td>
                  <td>{contact.phone || '-'}</td>
                  <td className="actions-col">
                    <button onClick={() => handleRemoveFromGroup(contact.id)}
                      className="btn-icon btn-danger"
                      title={removingContactId === contact.id ? 'Removing…' : 'Remove from group'}
                      disabled={removingContactId === contact.id}>
                      {removingContactId === contact.id ? (
                        <span className="material-symbols-rounded spinning">progress_activity</span>
                      ) : (
                        <span className="material-symbols-rounded">person_remove</span>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {groupContacts.length === 0 && <div className="no-results">No contacts found in this group</div>}
        </div>
        )}

        {showAddModal && createPortal(
          <div className="modal-overlay" onClick={() => { if (!isAddingToGroup) setShowAddModal(false); }}>
            <div className="modal-content modal-md" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">Add Contact to {selectedGroup}</h2>
                <button onClick={() => setShowAddModal(false)} className="modal-close" disabled={isAddingToGroup}>
                  <span className="material-symbols-rounded">close</span>
                </button>
              </div>
              <div className="modal-body" style={{ padding: "var(--spacing-lg) var(--spacing-xl) var(--spacing-xl)", minHeight: '475px' }}>
                {isAddingToGroup ? (
                  <div className="add-to-group-progress">
                    <LoadingBar progress={addProgress} message={addMessage} />
                  </div>
                ) : (
                  <div className="form-group">
                    <label>Select Contact</label>
                    <SearchableDropdown options={contactOptions} value={selectedContactToAdd}
                      onChange={setSelectedContactToAdd} placeholder="Search by account number or name..." />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button data-read-safe onClick={() => setShowAddModal(false)} className="btn-round btn-danger" disabled={isAddingToGroup}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                  <span className="btn-label">Cancel</span>
                </button>
                <button onClick={handleAddToGroup} className="btn-slideout" disabled={!selectedContactToAdd || isAddingToGroup}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">{isAddingToGroup ? 'progress_activity' : 'person_add'}</span>
                  </div>
                  <span className="btn-label">{isAddingToGroup ? 'Adding…' : 'Add to Group'}</span>
                </button>
              </div>
            </div>
          </div>, document.body
        )}

        <AlertDialog
          isOpen={alertDialog.isOpen}
          message={alertDialog.message}
          type={alertDialog.type}
          onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
        />

        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          type={confirmDialog.type}
          confirmText={confirmDialog.confirmText}
          onConfirm={() => { if (confirmDialog.onConfirm) confirmDialog.onConfirm(); }}
          onCancel={closeConfirm}
        />
      </div>
    );
  }

  // ─── Summary View ──────────────────────────────────────────────────────────

  // Any filter applied lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(
    { search: searchTerm, jointSearch, supplierSearch }, CLEARED_FILTERS,
    { labels: { jointSearch: 'Joint accounts', supplierSearch: 'Supplier groups' } }
  );
  const filtersApplied = headerFilters.count > 0;

  const clearFilters = () => {
    setSearchTerm(CLEARED_FILTERS.search);
    setJointSearch(CLEARED_FILTERS.jointSearch);
    setSupplierSearch(CLEARED_FILTERS.supplierSearch);
  };

  return (
    <div className="contact-groups cg-summary">
      <div className="search-header" style={{ padding: "0 0 var(--spacing-lg)", border: "none", margin: "0" }}>
        <div className="workflow-info" style={{ gridColumn: "1" }}>
          <span className="info">Manage contact groups, haulage job suppliers/ rates, joint accounts, and supplier groups</span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: "2" }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={clearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button onClick={() => setShowSyncWarning(true)} className="btn-slideout admin-item" disabled={syncProgress > 0}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">sync</span>
                </div>
                <span className="btn-label">Sync Groups from Xero</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {syncProgress > 0 && (
        <div style={{ padding: '0' }}>
          <LoadingBar progress={syncProgress} message={syncMessage} />
        </div>
      )}

      {/* Contact Groups — compact two-column grid */}
      <div className="groups-grid-compact">
        {groupSummaries.map(group => (
          <div key={group.group_name} className="group-card-compact" onClick={() => handleGroupClick(group.group_name)}>
            <div className="group-card-icon">
              <span className="material-symbols-rounded">groups</span>
            </div>
            <div className="group-card-info">
              <span className="group-card-name">{group.group_name}</span>
              <span className="group-card-count">{group.contact_count} contacts</span>
            </div>
            <span className="material-symbols-rounded group-card-arrow">chevron_right</span>
          </div>
        ))}
      </div>

      {/* Bottom row: Joint Accounts (left) + Supplier Groups (right) */}
      <div className="bottom-columns">
        {/* Joint Accounts */}
        <div className="section-panel">
          <div className="section-header">
            <h3>Joint Accounts</h3>
            <button onClick={() => setShowLinkModal(true)} className="btn-slideout">
              <div className="icon-wrapper"><span className="material-symbols-rounded">link</span></div>
              <span className="btn-label">Link Accounts</span>
            </button>
          </div>
          <div className="cg-panel-search">
            <Input
              type="text"
              value={jointSearch}
              onChange={(e) => setJointSearch(e.target.value)}
              placeholder="Search joint accounts..."
              icon="search"
            />
            <SearchHelp fields={['company', 'account no.', 'address (parent or linked account)']} />
          </div>
          <div className="section-panel-body">
            {renderParentChildList(visibleJoint, expandedParents, setExpandedParents, {
              showScopes: true,
              onToggleScope: handleToggleScope,
              showEdit: true,
              onEdit: (group) => setEditGroup({ type: 'joint', group })
            })}
          </div>
        </div>

        {/* Supplier Groups */}
        <div className="section-panel">
          <div className="section-header">
            <h3>Supplier Groups</h3>
            <button onClick={() => setShowSupplierGroupModal(true)} className="btn-slideout">
              <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
              <span className="btn-label">Create Group</span>
            </button>
          </div>
          <div className="cg-panel-search">
            <Input
              type="text"
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
              placeholder="Search supplier groups..."
              icon="search"
            />
            <SearchHelp fields={['supplier', 'account no.', 'address (parent or linked supplier)']} />
          </div>
          <div className="section-panel-body">
            {renderParentChildList(visibleSupplierGroups, expandedSupplierParents, setExpandedSupplierParents, {
              showRemove: true,
              onRemove: handleRemoveFromSupplierGroup,
              showAdd: true,
              showDelete: true,
              onDelete: handleDeleteSupplierGroup,
              showEdit: true,
              onEdit: (group) => setEditGroup({ type: 'supplier', group })
            })}
          </div>
        </div>
      </div>

      {/* Link Joint Accounts Modal */}
      {showLinkModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div style={{ height: "80vh" }} className="modal-content modal-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Link Joint Accounts</h2>
              <button onClick={() => setShowLinkModal(false)} className="modal-close">
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body tall" style={{ padding: "var(--spacing-lg) var(--spacing-xl) var(--spacing-xl)" }}>
              <div className="form-group" style={{ marginBottom: 'var(--spacing-lg)' }}>
                <label>Parent Account</label>
                <SearchableDropdown options={contactOptions} value={selectedParent} onChange={setSelectedParent}
                  placeholder="Search by account number or name..." />
              </div>
              <div className="form-group">
                <label>Add Child Account</label>
                <SearchableDropdown
                  options={contactOptions.filter(o => o.value !== parseInt(selectedParent) && !selectedJointChildren.includes(o.value))}
                  value=""
                  onChange={(val) => {
                    if (val && !selectedJointChildren.includes(val)) {
                      setSelectedJointChildren(prev => [...prev, val]);
                    }
                  }}
                  placeholder="Search children to add..." />
              </div>
              {selectedJointChildren.length > 0 && (
                <div className="selected-children-list">
                  <label>Children to add:</label>
                  {selectedJointChildren.map(childId => {
                    const c = allContacts.find(x => x.id === childId || x.id === parseInt(childId));
                    return (
                      <div key={childId} className="selected-child-tag">
                        <span>{c?.company_name || childId}</span>
                        <button onClick={() => setSelectedJointChildren(prev => prev.filter(id => id !== childId))}>
                          <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>close</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="form-group" style={{ marginTop: 'var(--spacing-lg)' }}>
                <label>These links apply to (all children)</label>
                <div className="scope-checks">
                  {LINK_SCOPES.map(s => (
                    <TickCheckbox key={s.key} id={`link-new-${s.key}`} size={16}
                      checked={linkScopes[s.key]}
                      onChange={(e) => setLinkScopes(prev => ({ ...prev, [s.key]: e.target.checked }))}
                      label={<span className="scope-check-label"><span className="material-symbols-rounded">{s.icon}</span>{s.label}</span>} />
                  ))}
                </div>
                <span className="link-scope-hint">
                  Pricing shares contract/spot prices across the group. Statements &amp; Reports roll the child into the parent. Invoicing always stays separate.
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <div>
                <button data-read-safe onClick={() => { setShowLinkModal(false); setSelectedParent(''); setSelectedJointChildren([]); }} className="btn-round btn-danger">
                  <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                  <span className="btn-label">Cancel</span>
                </button>
              </div>
              <div>
                <button onClick={createJointAccount} className="btn-slideout" disabled={!selectedParent || selectedJointChildren.length === 0}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">link</span></div>
                  <span className="btn-label">Link Accounts</span>
                </button>
              </div>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Create Supplier Group Modal */}
      {showSupplierGroupModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowSupplierGroupModal(false)}>
          <div style={{ height: "80vh" }} className="modal-content modal-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Create Supplier Group</h2>
              <button onClick={() => setShowSupplierGroupModal(false)} className="modal-close">
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body tall" style={{ padding: "var(--spacing-lg) var(--spacing-xl) var(--spacing-xl)" }}>
              <div className="form-group" style={{ marginBottom: 'var(--spacing-lg)' }}>
                <label>Parent Supplier</label>
                <SearchableDropdown options={supplierOptions} value={selectedSupplierParent}
                  onChange={setSelectedSupplierParent} placeholder="Search suppliers..." />
              </div>
              <div className="form-group">
                <label>Add Child Supplier</label>
                <SearchableDropdown
                  options={supplierOptions.filter(s => s.value !== parseInt(selectedSupplierParent))}
                  value=""
                  onChange={(val) => {
                    if (val && !selectedSupplierChildren.includes(val)) {
                      setSelectedSupplierChildren(prev => [...prev, val]);
                    }
                  }}
                  placeholder="Search suppliers to add..."
                />
              </div>
              {selectedSupplierChildren.length > 0 && (
                <div className="selected-children-list">
                  <label>Children to add:</label>
                  {selectedSupplierChildren.map(childId => {
                    const supplier = suppliers.find(s => s.id === childId || s.id === parseInt(childId));
                    return (
                      <div key={childId} className="selected-child-tag">
                        <span>{supplier?.company_name || childId}</span>
                        <button onClick={() => setSelectedSupplierChildren(prev => prev.filter(id => id !== childId))}>
                          <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>close</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <div>
                <button data-read-safe onClick={() => { setShowSupplierGroupModal(false); setSelectedSupplierParent(''); setSelectedSupplierChildren([]); }}
                  className="btn-round btn-danger">
                  <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                  <span className="btn-label">Cancel</span>
                </button>
              </div>
              <div>
                <button onClick={handleCreateSupplierGroup} className="btn-slideout"
                  disabled={!selectedSupplierParent || selectedSupplierChildren.length === 0}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
                  <span className="btn-label">Create Group</span>
                </button>
              </div>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Add Supplier to Group Modal */}
      {showAddSupplierModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowAddSupplierModal(false)}>
          <div className="modal-content modal-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add Supplier to Group</h2>
              <button onClick={() => setShowAddSupplierModal(false)} className="modal-close">
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: "var(--spacing-lg) var(--spacing-xl) var(--spacing-xl)", minHeight: '475px' }}>
              <div className="form-group">
                <label>Select Supplier</label>
                <SearchableDropdown options={supplierOptions} value={selectedSupplierToAdd}
                  onChange={setSelectedSupplierToAdd} placeholder="Search suppliers..." />
              </div>
            </div>
            <div className="modal-footer">
              <button data-read-safe onClick={() => setShowAddSupplierModal(false)} className="btn-round btn-danger" disabled={isAddingToSupplierGroup}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Cancel</span>
              </button>
              <button onClick={handleAddToSupplierGroup} className="btn-slideout" disabled={!selectedSupplierToAdd || isAddingToSupplierGroup}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">{isAddingToSupplierGroup ? 'progress_activity' : 'add'}</span>
                </div>
                <span className="btn-label">{isAddingToSupplierGroup ? 'Adding…' : 'Add to Group'}</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={showSyncWarning}
        title="Sync Contact Groups from Xero"
        message="This will overwrite all local contact group memberships with the latest from Xero."
        type="warning"
        confirmText="Sync"
        onConfirm={() => { setShowSyncWarning(false); syncContactGroups(); }}
        onCancel={() => setShowSyncWarning(false)}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        onConfirm={() => { if (confirmDialog.onConfirm) confirmDialog.onConfirm(); }}
        onCancel={closeConfirm}
      />

      {editGroup && (
        <GroupEditModal
          type={editGroup.type}
          group={editGroup.group}
          options={editGroup.type === 'joint' ? contactOptions : supplierOptions}
          onClose={() => setEditGroup(null)}
          onChanged={editGroup.type === 'joint' ? fetchJointAccounts : fetchSupplierGroups}
        />
      )}
    </div>
  );
}

export default ContactGroups;
